import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Paper,
    Select,
    TablePagination,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { Spinner } from '../../../components/UI';
import UploadPayEarnModal from '../../../components/Modals/UploadPayEarnModal';
import {
    getCapacityApprovals,
    approveCapacityRequest,
    rejectCapacityRequest,
    getCapacityApprovalById,
    supersedeCapacityRequest,
} from '../../../store/actions/settingsActionCreator';
import { showMessage } from '../../../utils/helper';
import { TIER_LABELS, RoutedToLine } from '../../../utils/capacityApproval';

// Same row-card list styling as Hubs & Shifts (HubList.js) — bold, non-uppercase headers and
// bordered/rounded/zebra-striped rows — so this Settings tab reads as one consistent system
// with the rest of Settings instead of its own bespoke table.
const useStyles = makeStyles(() => ({
    listItemHeader: {
        border: '0px',
        '& .MuiTypography-body1': {
            color: '#808080',
            fontSize: 16,
            fontWeight: 'bold',
        },
    },
    listItem: {
        padding: '16px',
        marginBottom: '8px',
        border: '1px solid #ebedf8',
        borderRadius: '8px',
        '& .MuiTypography-body1': {
            fontSize: 16,
            fontWeight: 400,
        },
        '&:nth-child(odd)': {
            backgroundColor: '#f8f9fa',
        },
        '&:hover': {
            backgroundColor: 'rgba(67,65,158,.1) !important',
        },
    },
}));

const STATUS_TAG = (status) => {
    const ok = status === 'APPROVED' || status === 'AUTO_APPLIED';
    return (
        <span
            style={{
                display: 'inline-block',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 12,
                fontWeight: 700,
                background: ok ? '#eaf6ee' : '#fdf5e6',
                color: ok ? '#15803d' : '#b45309',
                border: `1px solid ${ok ? '#bfe3cb' : '#f0dcb4'}`,
            }}
        >
            {status}
        </span>
    );
};

const BREACH_TAG = (count) => (
    <span
        style={{
            display: 'inline-block',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 12,
            fontWeight: 700,
            background: '#fdecea',
            color: '#c62828',
            border: '1px solid #f5c6cb',
        }}
    >
        {count} BREACHING
    </span>
);

// Both tables give every column an equal flex: 1 share of the row (see the Box style prop below)
// instead of hand-tuned percentages — Pending and History have the same column count, so this
// also keeps the "Hub · date / time" text wrapping identically between the two sections without
// having to keep two lists of magic-number widths in sync by hand.

// Every terminal status History can show — mirrors the backend's SCA_HISTORY_STATUSES allowlist.
const HISTORY_STATUS_OPTIONS = ['APPROVED', 'REJECTED', 'SUPERSEDED', 'AUTO_APPLIED', 'EXPIRED'];

const getRequestLabel = (row) => {
    if (row?.type === 'FILE') return `Slot file — ${row?.rows?.length ?? 0} rows`;
    // Hub name up front + date + start/end time uniquely identifies the slot — segment name
    // alone ("Evening") doesn't, since a hub can have more than one "Evening" slot, and an org
    // can have many hubs. Falls back gracefully (dropping whichever piece isn't available yet)
    // for requests raised before the backend started returning hub name / times.
    const hubPrefix = row?.hubName ? `${row.hubName} · ` : '';
    if (row?.slotDate && row?.slotStartTime && row?.slotEndTime) {
        return `${hubPrefix}${row.slotDate} · ${row.slotStartTime} - ${row.slotEndTime}`;
    }
    return row?.slotSegment && row?.slotDate
        ? `${hubPrefix}${row.slotSegment} · ${row.slotDate}`
        : `Shift ${row?.shiftId}`;
};

// Two explicit lines, not one string left to wrap wherever the column happens to be narrow
// enough to break — Pending's wider column let "hub · date · time" sit on one line while
// History's narrower one accidentally wrapped it after "date ·", so the two lists looked
// inconsistent even though they shared getRequestLabel. Always split hub+date from the slot
// time now, in both lists, so the layout no longer depends on string length vs. column width.
const renderRequestColumn = (row) => {
    if (row?.type === 'FILE') {
        return (
            <>
                <Typography style={{ fontWeight: 600 }}>{getRequestLabel(row)}</Typography>
                <Typography variant="body2" color="textSecondary">
                    {row?.orgName ?? `Org ${row?.orgId}`} · {row?.hubName ?? `Hub ${row?.hubId}`} · {row?.rows?.length ?? 0} rows
                </Typography>
            </>
        );
    }
    const hubPrefix = row?.hubName ? `${row.hubName} · ` : '';
    const hasTime = row?.slotDate && row?.slotStartTime && row?.slotEndTime;
    return (
        <>
            <Typography style={{ fontWeight: 600 }}>
                {hasTime
                    ? `${hubPrefix}${row.slotDate}`
                    : getRequestLabel(row)}
            </Typography>
            {hasTime && (
                <Typography variant="body2" color="textSecondary">
                    {row.slotStartTime} - {row.slotEndTime}
                </Typography>
            )}
        </>
    );
};

// currentCapacity is the slot's LIVE size right before this specific request (falls back to
// baselineCapacity server-side for legacy rows) — distinct from Original Size below, which is
// the released capacity and stays fixed even after other approved requests move the slot away
// from it. Using baselineCapacity here would show every request "from" the all-time-original
// size, which is wrong once a slot has had more than one approved change.
const getChangeLabel = (row) => {
    if (row?.type === 'FILE') return `${row?.rows?.length ?? 0} rows`;
    return `${row?.currentCapacity ?? '-'} → ${row?.requestedCapacity ?? '-'}`;
};

// baselineCapacity IS the released capacity — frozen at slot creation and never regenerated by
// later approvals, so it doubles as "original size" with no separate field needed from the backend.
const getOriginalSizeLabel = (row) => (row?.type === 'FILE' ? '-' : row?.baselineCapacity ?? '-');

const getBreachingCount = (row) => (row?.rows ?? []).filter((r) => r?.breached).length;

function ApprovalQueue({
    user,
    permissions,
    isCapacityApproverDecider,
    dispatchGetCapacityApprovals,
    dispatchApproveCapacityRequest,
    dispatchRejectCapacityRequest,
    dispatchGetCapacityApprovalById,
    dispatchSupersedeCapacityRequest,
}) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);
    const [detailRow, setDetailRow] = useState(null);
    const [uploadCorrectedRow, setUploadCorrectedRow] = useState(null);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
    const [historyTotalCount, setHistoryTotalCount] = useState(0);
    const [historyHubFilter, setHistoryHubFilter] = useState('');
    const [historyStatusFilter, setHistoryStatusFilter] = useState([]);
    const [hubOptions, setHubOptions] = useState({});

    // Pending is fetched in full every time (the backend tells us per-request whether it's
    // actually routed to the viewer via routedToMe) — it's bounded by how many requests are
    // CURRENTLY open. History is filtered (Hub, Outcome) and paginated server-side since it grows
    // unboundedly over the org's lifetime; the response combines "all pending" + "one filtered
    // page of history" into a single requests list, split client-side same as before.
    const fetchRequests = useCallback(() => {
        setLoading(true);
        dispatchGetCapacityApprovals(
            {
                historyPage,
                historySize: historyRowsPerPage,
                historyHubId: historyHubFilter || undefined,
                historyStatuses: historyStatusFilter.length ? historyStatusFilter.join(',') : undefined,
            },
            (response) => {
                setLoading(false);
                setRequests(response?.response?.requests ?? []);
                setHistoryTotalCount(response?.response?.historyTotalCount ?? 0);
                setHubOptions(response?.response?.hubOptions ?? {});
            },
            () => {
                setLoading(false);
            },
        );
    }, [
        dispatchGetCapacityApprovals,
        historyPage,
        historyRowsPerPage,
        historyHubFilter,
        historyStatusFilter,
    ]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleDecision = (id, action) => {
        setLoading(true);
        const dispatchFn =
            action === 'approve'
                ? dispatchApproveCapacityRequest
                : dispatchRejectCapacityRequest;
        dispatchFn(
            id,
            () => {
                showMessage(
                    action === 'approve' ? 'Request approved' : 'Request rejected',
                    'success',
                );
                fetchRequests();
            },
            () => {
                setLoading(false);
            },
        );
    };

    // "Upload Corrected File" — supersedes the old pending FILE request (flips status=SUPERSEDED,
    // no file touched server-side) then immediately opens the ordinary Add Slot upload for the
    // replacement, scoped to the same hub/org — matches the backend's own documented intent on
    // the /supersede endpoint. Requester can't do this (blocked server-side); button is already
    // hidden from them via the same !isOwnRequest(row) gate as Approve/Reject.
    const handleUploadCorrected = (row) => {
        setLoading(true);
        dispatchSupersedeCapacityRequest(
            row.id,
            () => {
                setLoading(false);
                showMessage('Old request superseded — upload the corrected file now', 'info');
                fetchRequests();
                setUploadCorrectedRow(row);
            },
            () => {
                setLoading(false);
            },
        );
    };

    const openDetail = (row) => {
        if (row?.type !== 'FILE') return;
        dispatchGetCapacityApprovalById(row?.id, (response) => {
            setDetailRow(response?.response ?? row);
        });
    };

    const isOwnRequest = (row) =>
        String(row?.requestedBy) === String(user?.id);

    const pending = requests.filter((r) => r?.status === 'PENDING' && r?.routedToMe && !isOwnRequest(r));
    const history = requests.filter((r) => r?.status !== 'PENDING');

    // bold defaults to withRole when omitted (Pending's original behavior) — History passes it
    // explicitly so its name column can be bold like Pending's without also picking up the role
    // subtitle line.
    const renderByColumn = (row, { withRole, bold }) => (
        <>
            <Typography style={{ fontWeight: (bold ?? withRole) ? 600 : 400 }}>
                {isOwnRequest(row) ? 'You' : row?.requestedByName ?? row?.requestedBy}
            </Typography>
            {withRole && (
                <Typography variant="body2" color="textSecondary">
                    {TIER_LABELS[row?.requesterRole] ?? row?.requesterRole ?? '-'}
                </Typography>
            )}
        </>
    );

    return (
        <Spinner loading={loading}>
            <Paper style={{ padding: '1.5rem' }} elevation={0}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                    Requests routed to you. First to act decides.
                </Typography>
                {pending.length ? (
                    <List component="nav">
                        <ListItem className={classes.listItemHeader}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                }}
                            >
                                <Box style={{ flex: 1.5 }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Request</Typography>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Requested by</Typography>
                                </Box>
                                <Box style={{ flex: 0.5 }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Original Size</Typography>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Change</Typography>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Reason</Typography>
                                </Box>
                                <Box style={{ flex: 1 }}> </Box>
                            </Box>
                        </ListItem>
                        {pending.map((row) => (
                            <ListItem
                                key={row.id}
                                className={classes.listItem}
                                style={{ cursor: row?.type === 'FILE' ? 'pointer' : 'default' }}
                                onClick={() => openDetail(row)}
                            >
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Box style={{ flex: 1.5 }}>
                                        {renderRequestColumn(row)}
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        {renderByColumn(row, { withRole: true })}
                                    </Box>
                                    <Box style={{ flex: 0.5 }}>
                                        <Typography>{getOriginalSizeLabel(row)}</Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Typography component="div">
                                            {row?.type === 'FILE'
                                                ? BREACH_TAG(getBreachingCount(row))
                                                : getChangeLabel(row)}
                                        </Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Typography>{row?.reason ?? '-'}</Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        {row?.type === 'FILE' ? (
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                disableElevation
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    borderColor: '#EFEFEF',
                                                    backgroundColor: '#EFEFEF',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDetail(row);
                                                }}
                                            >
                                                Review
                                            </Button>
                                        ) : (
                                            <Box style={{ whiteSpace: 'nowrap' }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    disableElevation
                                                    style={{ marginRight: 8 }}
                                                    disabled={!permissions?.update}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDecision(row.id, 'approve');
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    disableElevation
                                                    disabled={!permissions?.update}
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: '#f44336',
                                                        borderColor: '#f44336',
                                                        backgroundColor: '#fdecea',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDecision(row.id, 'reject');
                                                    }}
                                                >
                                                    Reject
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    !loading && (
                        <Box
                            style={{
                                marginTop: 24,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: 40,
                            }}
                        >
                            <Typography color="textSecondary">
                                Nothing awaiting your approval
                            </Typography>
                        </Box>
                    )
                )}
                {(!!historyTotalCount || !!historyHubFilter || !!historyStatusFilter.length) && (
                    <Box style={{ marginTop: 32 }}>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 8,
                            }}
                        >
                            <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                                History
                            </Typography>
                            <Box style={{ display: 'flex', gap: 8 }}>
                                <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                                    <InputLabel id="history-hub-filter-label">Hub</InputLabel>
                                    <Select
                                        labelId="history-hub-filter-label"
                                        label="Hub"
                                        value={historyHubFilter}
                                        onChange={(e) => {
                                            setHistoryHubFilter(e.target.value);
                                            setHistoryPage(0);
                                        }}
                                    >
                                        <MenuItem value="">All hubs</MenuItem>
                                        {Object.entries(hubOptions).map(([id, name]) => (
                                            <MenuItem key={id} value={id}>
                                                {name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                    <InputLabel id="history-outcome-filter-label">Outcome</InputLabel>
                                    <Select
                                        labelId="history-outcome-filter-label"
                                        label="Outcome"
                                        multiple
                                        value={historyStatusFilter}
                                        renderValue={(selected) =>
                                            selected.length ? selected.join(', ') : 'All outcomes'
                                        }
                                        onChange={(e) => {
                                            setHistoryStatusFilter(e.target.value);
                                            setHistoryPage(0);
                                        }}
                                    >
                                        {HISTORY_STATUS_OPTIONS.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                <Checkbox
                                                    size="small"
                                                    checked={historyStatusFilter.includes(status)}
                                                    style={{ padding: 0, marginRight: 8 }}
                                                />
                                                {status}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <List component="nav">
                            <ListItem className={classes.listItemHeader}>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <Box style={{ flex: 1.5 }}>
                                        <Typography style={{ fontWeight: 'bold' }}>Request</Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Typography style={{ fontWeight: 'bold' }}>Requested by</Typography>
                                    </Box>
                                    <Box style={{ flex: 0.5 }}>
                                        <Typography style={{ fontWeight: 'bold' }}>Original Size</Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Typography style={{ fontWeight: 'bold' }}>Change</Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Typography style={{ fontWeight: 'bold' }}>Outcome</Typography>
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Typography style={{ fontWeight: 'bold' }}>Decided by</Typography>
                                    </Box>
                                </Box>
                            </ListItem>
                            {!history.length && (
                                <Box style={{ padding: '24px 0', textAlign: 'center' }}>
                                    <Typography color="textSecondary">
                                        No history matches these filters
                                    </Typography>
                                </Box>
                            )}
                            {history.map((row) => (
                                <ListItem
                                    key={row.id}
                                    className={classes.listItem}
                                    style={{ cursor: row?.type === 'FILE' ? 'pointer' : 'default' }}
                                    onClick={() => openDetail(row)}
                                >
                                    <Box
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Box style={{ flex: 1.5 }}>
                                            {renderRequestColumn(row)}
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            {renderByColumn(row, { withRole: false, bold: true })}
                                        </Box>
                                        <Box style={{ flex: 0.5 }}>
                                            <Typography>{getOriginalSizeLabel(row)}</Typography>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <Typography>{getChangeLabel(row)}</Typography>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <Typography component="div">{STATUS_TAG(row?.status)}</Typography>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <Typography>
                                                {row?.decidedByName ?? '-'}
                                                {row?.status === 'SUPERSEDED' ? ' (uploaded corrected file)' : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={historyTotalCount}
                            rowsPerPage={historyRowsPerPage}
                            page={historyPage}
                            onChangePage={(e, newPage) => setHistoryPage(newPage)}
                            onChangeRowsPerPage={(e) => {
                                setHistoryRowsPerPage(parseInt(e.target.value, 10));
                                setHistoryPage(0);
                            }}
                        />
                    </Box>
                )}
            </Paper>
            {!!detailRow && (
                <Box
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1300,
                    }}
                    onClick={() => setDetailRow(null)}
                >
                    <Box
                        style={{
                            background: '#fff',
                            padding: 24,
                            borderRadius: 8,
                            maxWidth: 900,
                            width: '90%',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Typography variant="h6" style={{ marginBottom: 4 }}>
                            Review request
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: 16 }}
                        >
                            Submitted by{' '}
                            {isOwnRequest(detailRow) ? 'You' : detailRow?.requestedByName ?? detailRow?.requestedBy}
                            {detailRow?.requesterRole ? ` (${TIER_LABELS[detailRow.requesterRole] ?? detailRow.requesterRole})` : ''} ·{' '}
                            {detailRow?.orgName ?? `Org ${detailRow?.orgId}`} ·{' '}
                            {detailRow?.hubName ?? `Hub ${detailRow?.hubId}`} ·{' '}
                            {(detailRow?.rows ?? []).length} rows
                        </Typography>
                        {detailRow?.status === 'PENDING' && detailRow?.routedToTier && (
                            <Typography variant="body2" style={{ marginBottom: 16 }}>
                                {/* No approvers/roleChain fetch here (unlike EditCapacityModal/
                                    UploadPayEarnModal) — this queue doesn't otherwise need it, so
                                    approverNames is omitted; the escalation fields below are the
                                    only names/allowlist info this response actually carries. */}
                                <RoutedToLine
                                    routedToTier={detailRow.routedToTier}
                                    additionalApproverTier={detailRow.additionalApproverTier}
                                    additionalApproverNames={detailRow.additionalApproverNames}
                                    approverDeciderApplicable={detailRow.approverDeciderApplicable}
                                />
                            </Typography>
                        )}
                        <Box style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        {['Action', 'Org ID', 'Org Name', 'Hub ID', 'Hub Name', 'Slot', 'Date', 'Start', 'End', 'Released / Baseline', 'Requested', 'Δ', 'Reason'].map((h) => (
                                            <th
                                                key={h}
                                                style={{
                                                    textAlign: 'left',
                                                    padding: '8px',
                                                    borderBottom: '2px solid #ebedf8',
                                                    color: '#808080',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(detailRow?.rows ?? []).map((row, index) => {
                                        const isCreate = row?.action === 'CREATE';
                                        const delta =
                                            !isCreate &&
                                            row?.baselineCapacity != null &&
                                            row?.requestedCapacity != null
                                                ? row.requestedCapacity - row.baselineCapacity
                                                : null;
                                        return (
                                            <tr
                                                key={index}
                                                style={{
                                                    borderBottom: '1px solid #ebedf8',
                                                    background: row?.breached ? '#fff8f0' : undefined,
                                                }}
                                            >
                                                <td style={{ padding: '8px' }}>{row?.action ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>{row?.orgId ?? detailRow?.orgId}</td>
                                                <td style={{ padding: '8px' }}>{row?.orgName ?? detailRow?.orgName ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>{row?.hubId ?? detailRow?.hubId}</td>
                                                <td style={{ padding: '8px' }}>{row?.hubName ?? detailRow?.hubName ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>{row?.slotShortName ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>{row?.slotDate ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>{row?.slotStartTime ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>{row?.slotEndTime ?? '-'}</td>
                                                <td style={{ padding: '8px' }}>
                                                    {isCreate ? (
                                                        <em style={{ color: '#808080' }}>new slot</em>
                                                    ) : (
                                                        row?.baselineCapacity ?? '-'
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px', fontWeight: 600 }}>
                                                    {row?.requestedCapacity ?? '-'}
                                                </td>
                                                <td style={{ padding: '8px' }}>
                                                    {delta != null ? (delta > 0 ? `+${delta}` : delta) : '—'}
                                                </td>
                                                <td style={{ padding: '8px' }}>{detailRow?.reason ?? '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 16, marginBottom: 16 }}
                        >
                            Approving applies every row, including rows that were already within
                            limit. To use different numbers, upload a corrected file instead.
                        </Typography>
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 8,
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="default"
                                disableElevation
                                onClick={() => setDetailRow(null)}
                            >
                                Close
                            </Button>
                            {detailRow?.status === 'PENDING' && !isOwnRequest(detailRow) && (
                                <>
                                    {isCapacityApproverDecider && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            disableElevation
                                            disabled={!permissions?.update}
                                            onClick={() => {
                                                handleUploadCorrected(detailRow);
                                                setDetailRow(null);
                                            }}
                                        >
                                            Upload Corrected File
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        disableElevation
                                        disabled={!permissions?.update}
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: '#f44336',
                                            borderColor: '#f44336',
                                            backgroundColor: '#fdecea',
                                        }}
                                        onClick={() => {
                                            handleDecision(detailRow.id, 'reject');
                                            setDetailRow(null);
                                        }}
                                    >
                                        Reject
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disableElevation
                                        disabled={!permissions?.update}
                                        onClick={() => {
                                            handleDecision(detailRow.id, 'approve');
                                            setDetailRow(null);
                                        }}
                                    >
                                        Approve
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
            {!!uploadCorrectedRow && (
                <UploadPayEarnModal
                    open={true}
                    enumType="slots"
                    data={{
                        hubId: uploadCorrectedRow?.hubId,
                        orgId: uploadCorrectedRow?.orgId,
                    }}
                    onClose={() => {
                        setUploadCorrectedRow(null);
                        fetchRequests();
                    }}
                />
            )}
        </Spinner>
    );
}

const mapStateToProps = (state) => ({
    user: state?.user,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchGetCapacityApprovals: (params, onSuccess, onError) =>
        dispatch(getCapacityApprovals(params, onSuccess, onError)),
    dispatchApproveCapacityRequest: (id, onSuccess, onError) =>
        dispatch(approveCapacityRequest(id, onSuccess, onError)),
    dispatchRejectCapacityRequest: (id, onSuccess, onError) =>
        dispatch(rejectCapacityRequest(id, onSuccess, onError)),
    dispatchGetCapacityApprovalById: (id, onSuccess, onError) =>
        dispatch(getCapacityApprovalById(id, onSuccess, onError)),
    dispatchSupersedeCapacityRequest: (id, onSuccess, onError) =>
        dispatch(supersedeCapacityRequest(id, onSuccess, onError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalQueue);
